/**
 * Protected Route Component
 * Redirects to login if user is not authenticated
 * Optionally checks for required roles
 */

import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireRole?: string | string[]; // Optional role requirement (e.g., 'solas-admin' or ['admin', 'solas-admin'])
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireRole }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          <p className="mt-4 text-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Store the attempted location for redirect after login
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const finalRedirectUrl = encodeURIComponent(window.location.origin + location.pathname);
    window.location.href = `${apiBaseUrl}/login?final_redirect_url=${finalRedirectUrl}`;
    
    // Return loading state while redirecting
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light">
        <div className="text-center">
          <p className="text-text-muted">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Check if user needs onboarding (has "onboarding" role)
  // Redirect to onboarding page if they're not already there
  if (user) {
    const userRoles = user.roles || [];
    const hasOnboardingRole = userRoles.includes('onboarding');
    const isOnOnboardingPage = location.pathname === '/onboarding';
    
    if (hasOnboardingRole && !isOnOnboardingPage) {
      // User has onboarding role and is not on onboarding page - redirect them
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Check role requirement if specified
  if (requireRole && user) {
    const userRoles = user.roles || [];
    const requiredRoles = Array.isArray(requireRole) ? requireRole : [requireRole];
    if (!requiredRoles.some(role => userRoles.includes(role))) {
      // User doesn't have required role - redirect to dashboard with error
      return <Navigate to="/dashboard" replace />;
    }
  }

  // User is authenticated and has required role (if specified), render children
  return <>{children}</>;
};

export default ProtectedRoute;
